import { cloneElement, FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Divider, Typography } from '@mui/material';
import clsx from 'clsx';
import { useBreakpoints } from 'hooks';
import { useGetBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getSetBusinessPlan } from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';
import { useWorkspaceStore } from 'store/useWorkspaceStore';
import { getContextMenuId, getSetContextMenuId } from 'store/useWorkspaceStore/selectors';

import { ReactComponent as EllipsisVertical } from 'assets/icons/EllipsisVertical.svg';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { Button } from 'components/_basic/Button/Button';
import { TooltipComponent } from 'components/_basic/TooltipComponent/TooltipComponent';
import { UpgradePlanTooltip } from 'components/_basic/UpgradePlanTooltip';

import { ActionMenuContentView } from './components';
import { IBusinessPlanCardActionsMenuProps, IRenderButtonListProps, IRenderEachButtonProps } from './types';

import styles from './BusinessPlanCardActionsMenu.module.scss';

const RenderEachButton: FC<IRenderEachButtonProps> = ({
  title,
  icon,
  clickHandler,
  disabled,
  disabledActions,
  closeActionMenu,
  metadata,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();
  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  const parsedMetadata = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;

  const isUpdateOnlinePDFAvailable = parsedMetadata.updateOnlinePDFCount < 2 || isStandardOrLightTariff;
  const isDownloadPDFAvailable = parsedMetadata.downloadDocumentCount < 1 || isStandardOrLightTariff;

  const isDownloadOrUpdateOnlineTitle = title === 'PDF' || title === 'Update Online PDF';

  const { track, isPending } = usePromiseStatus();

  const isDeleteOption = !disabled && title === t('delete');

  const onClick = async () => {
    if (isPending) {
      return;
    }

    const clickResult = clickHandler();
    clickResult instanceof Promise ? await track(clickResult) : clickResult;
    closeActionMenu();
  };

  if (disabledActions === disabled) {
    return (
      <Box component="li" key={title} className={styles.listItem}>
        <TooltipComponent
          title={<UpgradePlanTooltip />}
          isTooltipHidden={
            !isDownloadOrUpdateOnlineTitle ||
            (title === 'PDF' && isDownloadPDFAvailable) ||
            (title === 'Update Online PDF' && isUpdateOnlinePDFAvailable)
          }
          trigger={isMobile ? 'click' : 'hover'}
          leaveDelay={isMobile ? 6500 : 200}
        >
          <Button
            fullWidth
            disabled={disabled}
            onClick={onClick}
            className={clsx(styles.button, { [styles.disabled]: disabled })}
            startIcon={cloneElement(icon, {
              className: clsx({ [styles.delete]: isDeleteOption }),
            })}
            isLoading={isPending}
          >
            <Typography
              variant="subtitle1"
              className={clsx(styles.titleStyles, {
                [styles.delete]: isDeleteOption,
                [styles.disabledTextStyle]: disabled,
              })}
            >
              {title}
            </Typography>
          </Button>
        </TooltipComponent>
      </Box>
    );
  }

  return null;
};

const RenderButtonList: FC<IRenderButtonListProps> = ({ actionsList, disabledActions, closeActionMenu, metadata }) => (
  <>
    {actionsList.map((eachAction) => (
      <RenderEachButton
        key={eachAction.id}
        {...eachAction}
        disabledActions={disabledActions}
        closeActionMenu={closeActionMenu}
        metadata={metadata}
      />
    ))}
  </>
);

export const BusinessPlanActionsMenu: FC<IBusinessPlanCardActionsMenuProps> = ({
  actionsList,
  uuid,
  docTitle,
  docType,
  metadata,
}) => {
  const { t } = useTranslation();

  const contextMenuId = useWorkspaceStore(getContextMenuId);
  const setContextMenuId = useWorkspaceStore(getSetContextMenuId);
  const { data: businessPlan } = useGetBusinessPlan({ id: uuid, enabled: true });
  const setBusinessPlan = useBusinessToolStore(getSetBusinessPlan);

  const anchorEl = useRef<SVGSVGElement | null>(null);

  const handleCloseClick = () => {
    setContextMenuId('');
  };

  const handleToggle = () => {
    if (contextMenuId === uuid) return handleCloseClick();

    setContextMenuId(uuid);
    setBusinessPlan(businessPlan!);
  };

  const isDisabledListItemPresent = useMemo(() => actionsList.some(({ disabled }) => disabled), [actionsList]);

  return (
    <Box>
      <TooltipComponent title={t('actions')} variant="s" className={styles.tooltipWrapper}>
        <EllipsisVertical className={styles.ellipsisStyle} ref={anchorEl} onClick={handleToggle} />
      </TooltipComponent>
      <ActionMenuContentView
        isOpened={contextMenuId === uuid}
        onClose={handleCloseClick}
        anchorEl={anchorEl}
        docTitle={docTitle}
        docType={docType}
        uuid={uuid}
      >
        <Box component="ul" className={styles.listContainer}>
          <RenderButtonList
            closeActionMenu={handleCloseClick}
            actionsList={actionsList}
            disabledActions={false}
            metadata={metadata}
          />
          {isDisabledListItemPresent && <Divider className={styles.divider} />}
          <RenderButtonList
            closeActionMenu={handleCloseClick}
            actionsList={actionsList}
            disabledActions
            metadata={metadata}
          />
        </Box>
      </ActionMenuContentView>
    </Box>
  );
};
